import { tokenManager } from '../utils/useToken';
import { InternalConfig, WindowState } from './types';

// SVG 图标常量
const SVG_FULLSCREEN_EXIT = '<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path></svg>';
const SVG_FULLSCREEN_ENTER = '<svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path></svg>';

export class ChatWindow {
  private element: HTMLDivElement | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private config: InternalConfig;
  private state: WindowState = 'closed';
  private onMinimize: () => void;
  private onClose: () => void;
  private onIframeLoad: ((iframe: HTMLIFrameElement) => void) | null = null;

  // 拖拽相关属性
  private isDragging: boolean = false;
  private isRefreshing: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private initialLeft: number = 0;
  private initialTop: number = 0;
  private boundMouseMove: (e: MouseEvent) => void;
  private boundMouseUp: (e: MouseEvent) => void;

  constructor(
    config: InternalConfig,
    onMinimize: () => void,
    onClose: () => void
  ) {
    this.config = config;
    this.onMinimize = onMinimize;
    this.onClose = onClose;
    
    // 绑定事件处理函数以保持 this 上下文
    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundMouseUp = this.onMouseUp.bind(this);
  }

  public create(): HTMLDivElement {
    if (this.element) {
      return this.element;
    }

    const window = document.createElement('div');
    window.className = 'floating-chat-window';
    if (this.config.fullScreen) {
      window.classList.add('is-fullscreen');
    }
    window.setAttribute('data-state', 'closed');
    window.setAttribute('data-position', this.config.position);
    window.setAttribute('data-theme', this.config.theme);
    window.style.width = typeof this.config.windowWidth === 'number' ? `${this.config.windowWidth}px` : this.config.windowWidth;
    window.style.height = typeof this.config.windowHeight === 'number' ? `${this.config.windowHeight}px` : this.config.windowHeight;

    // 创建标题栏
    const header = this.createHeader();
    window.appendChild(header);

    // 创建主体
    const body = this.createBody();
    window.appendChild(body);

    this.element = window;
    return window;
  }

  private createHeader(): HTMLDivElement {
    const header = document.createElement('div');
    header.className = 'window-header';
    header.style.cursor = 'move'; // 添加移动光标样式

    // 添加拖拽事件
    header.addEventListener('mousedown', (e: MouseEvent) => {
      // 如果点击的是控制按钮，不触发拖拽
      if ((e.target as HTMLElement).closest('.window-controls')) {
        return;
      }
      this.startDrag(e);
    });

    // 标题
    const title = document.createElement('span');
    title.className = 'window-title';
    title.textContent = this.config.title;

    // 控制按钮
    const controls = document.createElement('div');
    controls.className = 'window-controls';

    // 最小化按钮
    const minimizeBtn = document.createElement('button');
    minimizeBtn.className = 'btn-minimize';
    minimizeBtn.textContent = '−';
    minimizeBtn.title = '最小化';
    minimizeBtn.addEventListener('click', this.onMinimize);

    // 全屏按钮
    let fullscreenBtn: HTMLButtonElement | null = null;
    if (this.config.enableFullScreenToggle !== false) {
      fullscreenBtn = document.createElement('button');
      fullscreenBtn.className = 'btn-fullscreen';
      fullscreenBtn.innerHTML = this.config.fullScreen ? SVG_FULLSCREEN_EXIT : SVG_FULLSCREEN_ENTER;
      fullscreenBtn.title = this.config.fullScreen ? '退出全屏' : '全屏';
      fullscreenBtn.addEventListener('click', () => {
        if (fullscreenBtn) this.toggleFullScreen(fullscreenBtn);
      });
    }

    // 刷新按钮
    const refreshBtn = document.createElement('button');
    refreshBtn.className = 'btn-refresh';
    refreshBtn.textContent = '↻';
    refreshBtn.title = '刷新';
    refreshBtn.addEventListener('click', () => this.refreshIframe());

    // 关闭按钮
    const closeBtn = document.createElement('button');
    closeBtn.className = 'btn-close';
    closeBtn.textContent = '×';
    closeBtn.title = '关闭';
    closeBtn.addEventListener('click', this.onClose);

    controls.appendChild(minimizeBtn);
    if (fullscreenBtn) {
      controls.appendChild(fullscreenBtn);
    }
    controls.appendChild(refreshBtn);
    controls.appendChild(closeBtn);

    header.appendChild(title);
    header.appendChild(controls);

    return header;
  }

  private createBody(): HTMLDivElement {
    const body = document.createElement('div');
    body.className = 'window-body';

    // 创建加载状态
    const loading = this.createLoadingOverlay();
    body.appendChild(loading);

    return body;
  }

  private createLoadingOverlay(): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.className = 'loading-overlay';

    const spinner = document.createElement('div');
    spinner.className = 'loading-spinner';

    const text = document.createElement('div');
    text.className = 'loading-text';
    text.textContent = '正在加载...';

    overlay.appendChild(spinner);
    overlay.appendChild(text);

    return overlay;
  }

  private buildEmbedUrl(): string {

    const searchParams: any = {
      theme: this.config.theme,
      primaryColor: this.config.primaryColor,
      height: '100%',
      width: '100%',
      title: this.config.title,
      showBranding: this.config.showBranding.toString(),
      storageType: this.config.storageType, // 传递 storageType
    }

    // 支持loginInfo字符串或对象
    if(this.config.loginInfo){
      if(typeof this.config.loginInfo === 'string'){
        searchParams.loginInfo = this.config.loginInfo;
      } else {
        searchParams.loginInfo = JSON.stringify(this.config.loginInfo);
      }
    }
    if(this.config.parameters){
      searchParams.parameters =  JSON.stringify(this.config.parameters)
    }

    const params = new URLSearchParams({
      ...searchParams
    });

    if (this.config.agentId) {
      params.set('agentId', this.config.agentId);
    }
    const token = tokenManager.getToken()
    if (token) {
      params.set('token', token);
    }

    if (this.config.placeholder) {
      params.set('placeholder', this.config.placeholder);
    }

    return `${this.config.baseUrl}/embed?${params.toString()}`;
  }

  private createIframe(): void {
    if (this.iframe || !this.element) return;

    const body = this.element.querySelector('.window-body');
    if (!body) return;

    this.iframe = document.createElement('iframe');
    this.iframe.src = this.buildEmbedUrl();
    this.iframe.style.width = '100%';
    this.iframe.style.height = '100%';
    this.iframe.setAttribute('allowfullscreen', 'true');
    this.iframe.setAttribute('allow', 'microphone; camera; clipboard-write');

    // iframe加载完成后移除加载状态并触发回调
    this.iframe.addEventListener('load', () => {
      this.isRefreshing = false;
      const loading = body.querySelector('.loading-overlay');
      if (loading) {
        loading.remove();
      }
      if (this.onIframeLoad && this.iframe) {
        this.onIframeLoad(this.iframe);
      }
    });

    body.appendChild(this.iframe);
  }

  public loadIframe(): void {
    this.createIframe();
  }

  public refreshIframe(): void {
    if (!this.element || this.isRefreshing) return;

    const body = this.element.querySelector('.window-body');
    if (!body) return;

    this.isRefreshing = true;

    // 移除现有 iframe
    if (this.iframe) {
      this.iframe.remove();
      this.iframe = null;
    }

    // 清除已有的错误遮罩
    const existingError = body.querySelector('.auth-error-overlay');
    if (existingError) {
      existingError.remove();
    }

    // 重新显示加载状态
    const existingLoading = body.querySelector('.loading-overlay');
    if (existingLoading) {
      existingLoading.remove();
    }
    const loading = this.createLoadingOverlay();
    body.appendChild(loading);

    // 重新创建 iframe
    this.createIframe();
  }

  public showAuthError(message: string): void {
    if (!this.element) return;

    const body = this.element.querySelector('.window-body');
    if (!body) return;

    // 移除 loading overlay
    const loading = body.querySelector('.loading-overlay');
    if (loading) {
      loading.remove();
    }

    // 显示错误信息
    const errorOverlay = document.createElement('div');
    errorOverlay.className = 'auth-error-overlay';
    errorOverlay.innerHTML = `
      <div class="error-content">
        <div class="error-icon">⚠️</div>
        <div class="error-message">${message}</div>
        <div class="error-hint">请刷新页面后重试</div>
      </div>
    `;
    body.appendChild(errorOverlay);
  }

  public open(): void {
    if (!this.element || this.state === 'open' || this.state === 'opening') {
      return;
    }

    // 每次打开时, 强制同步当前的 config.fullScreen 状态到 UI 上
    const isCurrentlyFullscreen = this.element.classList.contains('is-fullscreen');
      console.log(this.config.fullScreen,isCurrentlyFullscreen,'更新状态');
      
    if (this.config.fullScreen && !isCurrentlyFullscreen) {
      this.element.classList.add('is-fullscreen');
      document.body.classList.add('floating-chat-fullscreen-mode');
      const fullscreenBtn = this.element.querySelector('.btn-fullscreen') as HTMLButtonElement;
      if (fullscreenBtn) {
        fullscreenBtn.innerHTML = SVG_FULLSCREEN_EXIT;
        fullscreenBtn.title = '退出全屏';
      }
    } else if (!this.config.fullScreen && isCurrentlyFullscreen) {
      this.element.classList.remove('is-fullscreen');
      document.body.classList.remove('floating-chat-fullscreen-mode');
      const fullscreenBtn = this.element.querySelector('.btn-fullscreen') as HTMLButtonElement;
      if (fullscreenBtn) {
        fullscreenBtn.innerHTML = SVG_FULLSCREEN_ENTER;
        fullscreenBtn.title = '全屏';
      }
    }

    // 不在这里创建 iframe，等待认证完成后再创建
    // this.createIframe();

    this.state = 'opening';
    this.element.setAttribute('data-state', 'opening');

    // 动画结束后设置为open状态
    setTimeout(() => {
      if (this.element && this.state === 'opening') {
        this.state = 'open';
        this.element.setAttribute('data-state', 'open');
      }
    }, 300);
  }

  public close(): void {
    if (!this.element || this.state === 'closed' || this.state === 'closing') {
      return;
    }

    this.state = 'closing';
    this.element.setAttribute('data-state', 'closing');
    
    // 关闭时移除 body 上的全屏滚动锁定
    document.body.classList.remove('floating-chat-fullscreen-mode');

    // 动画结束后设置为closed状态
    setTimeout(() => {
      if (this.element && this.state === 'closing') {
        this.state = 'closed';
        this.element.setAttribute('data-state', 'closed');
      }
    }, 300);
  }

  public isOpen(): boolean {
    return this.state === 'open' || this.state === 'opening';
  }

  public getState(): WindowState {
    return this.state;
  }

  public setOnIframeLoad(callback: (iframe: HTMLIFrameElement) => void): void {
    this.onIframeLoad = callback;
  }

  public getIframe(): HTMLIFrameElement | null {
    return this.iframe;
  }

  public updateTheme(theme: 'light' | 'dark'): void {
    if (this.element) {
      this.element.setAttribute('data-theme', theme);
    }
  }

  private toggleFullScreen(btn: HTMLButtonElement): void {
    if (!this.element) return;
    
    const isCurrentlyFullscreen = this.element.classList.contains('is-fullscreen');
    
    if (!isCurrentlyFullscreen) {
      this.element.classList.add('is-fullscreen');
      document.body.classList.add('floating-chat-fullscreen-mode');
      btn.innerHTML = SVG_FULLSCREEN_EXIT;
      btn.title = '退出全屏';
    } else {
      this.element.classList.remove('is-fullscreen');
      document.body.classList.remove('floating-chat-fullscreen-mode');
      btn.innerHTML = SVG_FULLSCREEN_ENTER;
      btn.title = '全屏';
    }
  }

  private startDrag(e: MouseEvent): void {
    if (!this.element || this.element.classList.contains('is-fullscreen')) return;
    
    // 只有鼠标左键触发拖拽
    if (e.button !== 0) return;

    this.isDragging = true;
    
    // 获取当前位置和鼠标位置
    const rect = this.element.getBoundingClientRect();
    this.initialLeft = rect.left;
    this.initialTop = rect.top;
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;

    // 切换定位方式为固定坐标
    // 必须清除 bottom/right 定位，改为 left/top
    this.element.style.left = `${rect.left}px`;
    this.element.style.top = `${rect.top}px`;
    this.element.style.bottom = 'auto';
    this.element.style.right = 'auto';
    this.element.style.margin = '0'; // 防止 margin 干扰
    
    // 添加全局事件监听
    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
    
    // 防止选中文本
    e.preventDefault();
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.isDragging || !this.element) return;

    const deltaX = e.clientX - this.dragStartX;
    const deltaY = e.clientY - this.dragStartY;

    let newLeft = this.initialLeft + deltaX;
    let newTop = this.initialTop + deltaY;

    // 边界检查
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const elementWidth = this.element.offsetWidth;
    const elementHeight = this.element.offsetHeight;

    // 限制左边界
    if (newLeft < 0) newLeft = 0;
    // 限制右边界
    if (newLeft + elementWidth > windowWidth) {
      newLeft = windowWidth - elementWidth;
    }
    
    // 限制上边界
    if (newTop < 0) newTop = 0;
    // 限制下边界
    if (newTop + elementHeight > windowHeight) {
      newTop = windowHeight - elementHeight;
    }

    this.element.style.left = `${newLeft}px`;
    this.element.style.top = `${newTop}px`;
  }

  private onMouseUp(e: MouseEvent): void {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
  }

  public destroy(): void {
    // 移除可能的全局监听器
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);

    if (this.element) {
      const minimizeBtn = this.element.querySelector('.btn-minimize');
      const refreshBtn = this.element.querySelector('.btn-refresh');
      const closeBtn = this.element.querySelector('.btn-close');

      if (minimizeBtn) {
        minimizeBtn.removeEventListener('click', this.onMinimize);
      }
      if (refreshBtn) {
        refreshBtn.replaceWith(refreshBtn.cloneNode(true));
      }
      if (closeBtn) {
        closeBtn.removeEventListener('click', this.onClose);
      }

      this.element.remove();
      this.element = null;
    }

    this.iframe = null;
  }

  public getElement(): HTMLDivElement | null {
    return this.element;
  }
}
